import * as XLSX from 'xlsx/xlsx.mjs'

class ExportFileService {
  /**
   * Exporta os dados para um arquivo XLSX.
   *
   * Suporta valores simples, células com fórmulas e linhas extras
   * antes ou depois dos dados.
   *
   * @param {Object} options Configurações da exportação.
   * @param {string} options.filename Nome do arquivo.
   * @param {string} options.sheetName Nome da planilha.
   * @param {Array} options.columns Colunas da tabela.
   * @param {Array} options.rows Dados da tabela.
   * @param {Array} options.beforeRows Linhas extras antes da tabela.
   * @param {Array} options.afterRows Linhas extras depois da tabela.
   */
  static exportXls({
    filename = 'arquivo',
    sheetName = 'Planilha',
    columns = [],
    rows = [],
    beforeRows = [],
    afterRows = [],
  }) {
    const worksheet = {}

    const headerRowIndex = beforeRows.length
    const dataStartRowIndex = headerRowIndex + 1
    const dataEndRowIndex = dataStartRowIndex + rows.length - 1
    const afterRowsStartIndex = dataStartRowIndex + rows.length

    const context = {
      headerRow: headerRowIndex + 1,
      dataStartRow: dataStartRowIndex + 1,
      dataEndRow: dataEndRowIndex + 1,
    }

    let maxRowIndex = 0
    let maxColumnIndex = Math.max(columns.length - 1, 0)

    beforeRows.forEach((row, rowIndex) => {
      const result = this.writeCustomRow({
        worksheet,
        row,
        rowIndex,
        context,
      })

      maxRowIndex = Math.max(maxRowIndex, result.maxRowIndex)
      maxColumnIndex = Math.max(maxColumnIndex, result.maxColumnIndex)
    })

    columns.forEach((column, columnIndex) => {
      this.writeCell({
        worksheet,
        rowIndex: headerRowIndex,
        columnIndex,
        value: column.label,
        context: {
          ...context,
          row: headerRowIndex + 1,
        },
      })
    })

    maxRowIndex = Math.max(maxRowIndex, headerRowIndex)

    rows.forEach((row, rowIndex) => {
      const excelRowIndex = dataStartRowIndex + rowIndex

      columns.forEach((column, columnIndex) => {
        const value = row[column.key]

        this.writeCell({
          worksheet,
          rowIndex: excelRowIndex,
          columnIndex,
          value,
          context: {
            ...context,
            row: excelRowIndex + 1,
          },
        })
      })

      maxRowIndex = Math.max(maxRowIndex, excelRowIndex)
    })

    afterRows.forEach((row, index) => {
      const rowIndex = afterRowsStartIndex + index

      const result = this.writeCustomRow({
        worksheet,
        row,
        rowIndex,
        context: {
          ...context,
          row: rowIndex + 1,
        },
      })

      maxRowIndex = Math.max(maxRowIndex, result.maxRowIndex)
      maxColumnIndex = Math.max(maxColumnIndex, result.maxColumnIndex)
    })

    worksheet['!ref'] = XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: maxRowIndex, c: maxColumnIndex },
    })

    worksheet['!cols'] = this.getColumnWidths(columns, maxColumnIndex)

    const workbook = XLSX.utils.book_new()

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      this.sanitizeSheetName(sheetName)
    )

    XLSX.writeFile(workbook, `${this.sanitizeFilename(filename)}.xlsx`)
  }

  /**
   * Exporta os dados para um arquivo CSV.
   *
   * Caso uma célula contenha fórmula, será utilizado o valor de "result",
   * pois CSV não suporta fórmulas.
   *
   * @param {Object} options Configurações da exportação.
   * @param {string} options.filename Nome do arquivo.
   * @param {Array} options.columns Colunas da tabela.
   * @param {Array} options.rows Dados da tabela.
   * @param {Array} options.beforeRows Linhas extras antes da tabela.
   * @param {Array} options.afterRows Linhas extras depois da tabela.
   */
  static exportCsv({
    filename = 'arquivo',
    columns = [],
    rows = [],
    beforeRows = [],
    afterRows = [],
  }) {
    const header = columns.map((column) => column.label)

    const data = rows.map((row) => {
      return columns.map((column) => {
        return this.getCsvValue(row[column.key])
      })
    })

    const beforeData = beforeRows.map((row) => this.customRowToArray(row))
    const afterData = afterRows.map((row) => this.customRowToArray(row))

    const worksheet = XLSX.utils.aoa_to_sheet([
      ...beforeData,
      header,
      ...data,
      ...afterData,
    ])

    const csv = XLSX.utils.sheet_to_csv(worksheet, { FS: ';' })

    this.downloadBlob({
      content: '\ufeff' + csv,
      filename: `${this.sanitizeFilename(filename)}.csv`,
      type: 'text/csv;charset=utf-8;',
    })
  }

  /**
   * Escreve uma linha personalizada na planilha.
   *
   * @param {Object} options Configurações da linha.
   * @param {Object} options.worksheet Planilha.
   * @param {Object|Array} options.row Linha personalizada.
   * @param {number} options.rowIndex Índice da linha.
   * @param {Object} options.context Contexto das fórmulas.
   * @returns {Object}
   */
  static writeCustomRow({ worksheet, row, rowIndex, context }) {
    let maxColumnIndex = 0

    const cells = this.normalizeCustomRow(row)

    cells.forEach((cell) => {
      const columnIndex = this.columnToIndex(cell.column)

      this.writeCell({
        worksheet,
        rowIndex,
        columnIndex,
        value: cell.value,
        context: {
          ...context,
          row: rowIndex + 1,
        },
      })

      maxColumnIndex = Math.max(maxColumnIndex, columnIndex)
    })

    return {
      maxRowIndex: rowIndex,
      maxColumnIndex,
    }
  }

  /**
   * Escreve uma célula na planilha.
   *
   * @param {Object} options Configurações da célula.
   * @param {Object} options.worksheet Planilha.
   * @param {number} options.rowIndex Índice da linha.
   * @param {number} options.columnIndex Índice da coluna.
   * @param {*} options.value Valor da célula.
   * @param {Object} options.context Contexto das fórmulas.
   */
  static writeCell({ worksheet, rowIndex, columnIndex, value, context }) {
    if (value === null || value === undefined) {
      return
    }

    const cellAddress = XLSX.utils.encode_cell({
      r: rowIndex,
      c: columnIndex,
    })

    if (this.isFormulaCell(value)) {
      worksheet[cellAddress] = {
        t: value.type || 'n',
        f: this.parseFormula(value.formula, context),
      }

      if (value.result !== undefined) {
        worksheet[cellAddress].v = value.result
      }

      this.applyCellFormat(worksheet[cellAddress], value)

      return
    }

    worksheet[cellAddress] = this.createCell(value)
  }

  /**
   * Cria uma célula simples.
   *
   * @param {*} value Valor da célula.
   * @returns {Object}
   */
  static createCell(value) {
    if (typeof value === 'number') {
      return {
        t: 'n',
        v: value,
      }
    }

    if (typeof value === 'boolean') {
      return {
        t: 'b',
        v: value,
      }
    }

    if (value instanceof Date) {
      return {
        t: 'd',
        v: value,
      }
    }

    return {
      t: 's',
      v: String(value),
    }
  }

  /**
   * Normaliza uma linha personalizada.
   *
   * Aceita:
   * - Array: ['texto', '', 10]
   * - Object: { B: 'Média eSF', D: 46.11 }
   * - Object com cells: { cells: [{ column: 'B', value: 'Média eSF' }] }
   *
   * @param {Object|Array} row Linha personalizada.
   * @returns {Array}
   */
  static normalizeCustomRow(row) {
    if (Array.isArray(row)) {
      return row.map((value, index) => ({
        column: index,
        value,
      }))
    }

    if (row && Array.isArray(row.cells)) {
      return row.cells
    }

    if (row && typeof row === 'object') {
      return Object.keys(row).map((column) => ({
        column,
        value: row[column],
      }))
    }

    return []
  }

  /**
   * Converte uma linha personalizada para array.
   *
   * @param {Object|Array} row Linha personalizada.
   * @returns {Array}
   */
  static customRowToArray(row) {
    const cells = this.normalizeCustomRow(row)
    const data = []

    cells.forEach((cell) => {
      const columnIndex = this.columnToIndex(cell.column)
      data[columnIndex] = this.getCsvValue(cell.value)
    })

    return data
  }

  /**
   * Retorna o valor usado no CSV.
   *
   * @param {*} value Valor da célula.
   * @returns {*}
   */
  static getCsvValue(value) {
    if (this.isFormulaCell(value)) {
      return value.result ?? ''
    }

    return value ?? ''
  }

  /**
   * Verifica se o valor representa uma célula com fórmula.
   *
   * @param {*} value Valor da célula.
   * @returns {boolean}
   */
  static isFormulaCell(value) {
    return value && typeof value === 'object' && value.formula
  }

  /**
   * Substitui variáveis dinâmicas da fórmula.
   *
   * Suporta:
   * - {row}
   * - {row+1}
   * - {row-1}
   * - {headerRow}
   * - {dataStartRow}
   * - {dataStartRow+1}
   * - {dataEndRow}
   * - {dataEndRow-1}
   *
   * @param {string} formula Fórmula original.
   * @param {Object} context Contexto da fórmula.
   * @returns {string}
   */
  static parseFormula(formula, context = {}) {
    return String(formula || '').replace(
      /\{(row|headerRow|dataStartRow|dataEndRow)([+-]\d+)?\}/g,
      (match, key, offset) => {
        const value = Number(context[key] || 0)
        const difference = Number(offset || 0)

        return String(value + difference)
      }
    )
  }

  /**
   * Converte uma coluna para índice numérico.
   *
   * @param {string|number} column Coluna em letra ou índice.
   * @returns {number}
   */
  static columnToIndex(column) {
    if (typeof column === 'number') {
      return column
    }

    return XLSX.utils.decode_col(String(column).toUpperCase())
  }

  /**
   * Retorna a largura das colunas.
   *
   * @param {Array} columns Colunas da tabela.
   * @param {number} maxColumnIndex Maior índice de coluna usado.
   * @returns {Array}
   */
  static getColumnWidths(columns, maxColumnIndex) {
    const widths = []

    for (let index = 0; index <= maxColumnIndex; index++) {
      widths.push({
        wch: columns[index]?.width || 20,
      })
    }

    return widths
  }

  /**
   * Realiza o download de um arquivo no navegador.
   *
   * @param {Object} options Configurações do download.
   * @param {BlobPart|string} options.content Conteúdo do arquivo.
   * @param {string} options.filename Nome do arquivo.
   * @param {string} options.type Tipo MIME do arquivo.
   */
  static downloadBlob({ content, filename, type }) {
    const blob = new Blob([content], { type })
    const url = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.click()

    URL.revokeObjectURL(url)
  }

  /**
   * Remove caracteres inválidos do nome do arquivo.
   *
   * @param {string} filename Nome do arquivo.
   * @returns {string}
   */
  static sanitizeFilename(filename) {
    return String(filename || 'arquivo')
      .replace(/[\\/:*?"<>|]/g, '-')
      .replace(/\s+/g, ' ')
      .trim()
  }

  /**
   * Remove caracteres inválidos e limita o nome da planilha
   * ao tamanho máximo suportado pelo Excel.
   *
   * @param {string} sheetName Nome da planilha.
   * @returns {string}
   */
  static sanitizeSheetName(sheetName) {
    return String(sheetName || 'Planilha')
      .replace(/[\\/?*[\]:]/g, '-')
      .substring(0, 31)
  }

  /**
   * Aplica formatações adicionais na célula.
   *
   * @param {Object} cell Célula do SheetJS.
   * @param {Object} value Configurações da célula.
   */
  static applyCellFormat(cell, value) {
    if (value.numberFormat) {
      cell.z = value.numberFormat
    }
  }
}

export default ExportFileService
